import React, { useState, useRef,useEffect } from 'react';
import PropTypes from 'prop-types';
import { dataLayerSendEvent } from 'Epharmacy/EpharmacyAnalytics';
import { RadioButton, InputFieldControl } from '@common/Component/Forms';
import RightSideBar from '@common/Component/RightSideBar/RightSideBar';
import ToggleSwitch from '@common/Component/ToggleSwitch';
import OpticalVariantStyled from './OpticalVariantStyled';
import { useTranslation } from '@common/i18n';
 
const OpticalVariant = ({ sphOptions, selectedSPHSkus, setSelectedSPHSkus, variants, errors, productDetails }) => {
  const [showSPHSideBar, setShowSPHSideBar] = useState(false);
  const [isDifferentSPH, setIsDifferentSPH] = useState(false);
  const [selectedLeftOrDefaultSPH, setSelectedLeftOrDefaultSPH] = useState();
  const [selectedRightSPH, setSelectedRightSPH] = useState();
  const isLeftOrDefaultSPHRef = useRef(true);
  const defaultSPHRef = useRef();
  const t = useTranslation();

  useEffect(() => {
    if(selectedSPHSkus === null) {
      setIsDifferentSPH(false);
    }
  }, [selectedSPHSkus])

  const renderSPHOptionsDropdown = () => {
    if (!isDifferentSPH) {
      return (
        <InputFieldControl
          isLabel
          enableFullWidth
          label={t('ProductDetail.selectSPH')}
          textType="text"
          placeholder={t('ProductDetail.selectSPH')}
          data-test="sphDefault"
          name="sphDefault"
          valueType={selectedSPHSkus?.leftOrDefault?.displayText}
          customClass="dropdownInput"
          isInvalid={errors?.sph?.leftOrDefaultInvalid}
          onClick={() => {
            isLeftOrDefaultSPHRef.current = true;
            setShowSPHSideBar(true);
            defaultSPHRef?.current?.focus();
            if (selectedSPHSkus?.leftOrDefault?.id) {
              setSelectedLeftOrDefaultSPH(
                `${selectedSPHSkus?.leftOrDefault?.id}|${selectedSPHSkus?.leftOrDefault?.displayText}`,
              );
            }
          }}
        />
      );
    }
    return (
      <>
        <InputFieldControl
          isLabel
          enableFullWidth
          label={t('ProductDetail.rightSPH')}
          textType="text"
          placeholder={t('ProductDetail.rightSPH')}
          data-test="sphRight"
          name="sphDefault"
          valueType={selectedSPHSkus?.right?.displayText}
          customClass="dropdownInput"
          isInvalid={errors?.sph?.rightInvalid}
          onClick={() => {
            isLeftOrDefaultSPHRef.current = false;
            setShowSPHSideBar(true);
            defaultSPHRef?.current?.focus();
          }}
        />
        &nbsp;&nbsp;
        <InputFieldControl
          isLabel
          enableFullWidth
          label={t('ProductDetail.leftSPH')}
          textType="text"
          placeholder={t('ProductDetail.leftSPH')}
          data-test="sphDefault"
          name="sphLeft"
          valueType={selectedSPHSkus?.leftOrDefault?.displayText}
          customClass="dropdownInput"
          isInvalid={errors?.sph?.leftOrDefaultInvalid}
          onClick={() => {
            isLeftOrDefaultSPHRef.current = true;
            setShowSPHSideBar(true);
            defaultSPHRef?.current?.focus();
          }}
        />
      </>
    );
  };

  const isSPHOptionSelected = (item) =>
    isLeftOrDefaultSPHRef?.current
      ? `${item?.id}|${item?.value}` === selectedLeftOrDefaultSPH
      : `${item?.id}|${item?.value}` === selectedRightSPH;

  const renderSPHOptionsList = () =>
    sphOptions?.map((item) => (
      <div key={item?.id} className="sph-option-item">
        <RadioButton
          className={`radiobutton ${isSPHOptionSelected(item) && 'radiobutton-selected'}`}
          name={`sphOptions-${item?.id}`}
          id={`sphOptions-${item?.id}`}
          label={item?.value}
          value={`${item?.id}|${item?.value}`}
          checked={isSPHOptionSelected(item)}
          onClick={(e) => {
            if (isLeftOrDefaultSPHRef?.current) {
              setSelectedLeftOrDefaultSPH(e.target.value);
            } else {
              setSelectedRightSPH(e.target.value);
            }
          }}
        />
      </div>
    ));

  const renderSPHSideBar = () => {
    if (showSPHSideBar) {
      return (
        <RightSideBar
          modalTitle={t('ProductDetail.spherical')}
          modalSubTitle={t('ProductDetail.selectEyePower')}
          size="md"
          closeButton
          customClass="sphOptionsModal"
          handleClose={() => {
            if (selectedSPHSkus?.leftOrDefault?.id) {
              setSelectedLeftOrDefaultSPH(
                `${selectedSPHSkus?.leftOrDefault?.id}|${selectedSPHSkus?.leftOrDefault?.displayText}`,
              );
            } else {
              setSelectedLeftOrDefaultSPH(null);
            }

            if (selectedSPHSkus?.right?.id) {
              setSelectedRightSPH(`${selectedSPHSkus?.right?.id}|${selectedSPHSkus?.right?.displayText}`);
            } else {
              setSelectedRightSPH(null);
            }
            setShowSPHSideBar(false);
          }}
          primaryButtonText={t('ProductDetail.Continue')}
          isPrimaryButtonDisabled={isLeftOrDefaultSPHRef.current ? !selectedLeftOrDefaultSPH : !selectedRightSPH}
          primaryButtonClicked={() => {
            const selectedSPHs = {};
            if (selectedLeftOrDefaultSPH) {
              const splitData = selectedLeftOrDefaultSPH?.split('|');
              selectedSPHs.leftOrDefault = {
                id: splitData[0],
                displayText: splitData[1],
                sku: variants[splitData[0]]?.sku,
              };
            }

            if (selectedRightSPH) {
              const splitData = selectedRightSPH?.split('|');

              selectedSPHs.right = {
                id: splitData[0],
                displayText: splitData[1],
                sku: variants[splitData[0]]?.sku,
              };
            }

            selectedSPHs.isDifferentSPH = isDifferentSPH;

            setSelectedSPHSkus(selectedSPHs);
            setShowSPHSideBar(false);
          }}
          footerBtnFullWidth
          appVariant="primary"
        >
          <div className="sph-options-list">{renderSPHOptionsList()}</div>
        </RightSideBar>
      );
    }

    return null;
  };

  const handleToggleChangeEvent = () => {
    const toggleParams = {
      screen_name: productDetails?.name,
      page_type: 'product details page',
      product_name: productDetails?.name,
      product_sku: productDetails?.sku,
      selection: !isDifferentSPH ? 'enable' : 'disable',
      myaster_category: 'epharmacy',
    };
    dataLayerSendEvent('eye_power_toggle', toggleParams);
  };

  return (
    <OpticalVariantStyled>
      {renderSPHSideBar()}
      <div className="variantBox">
        <div className="titleBar">{t('ProductDetail.eyePower')}</div>
        <div className="different-power">
          <div>{t('ProductDetail.differentPower')}</div>
          <div>
            <ToggleSwitch
              ref={defaultSPHRef}
              type="checkbox"
              checked={isDifferentSPH}
              onChange={() => {
                setIsDifferentSPH(!isDifferentSPH);
                setSelectedSPHSkus({
                  isDifferentSPH: !isDifferentSPH,
                });
                isLeftOrDefaultSPHRef.current = true;
                setSelectedRightSPH(null);
                setSelectedLeftOrDefaultSPH(null);
                handleToggleChangeEvent();
              }}
            />
          </div>
        </div>
        <div className="spherical-options-section">
          <div className="spherical-label">
            <div className="spherical-label">{t('ProductDetail.spherical')}</div>
            <div className="sph-label">{t('ProductDetail.sph')}</div>
          </div>
          <div className="options-group">
            <div className="options-dropdown">{renderSPHOptionsDropdown()}</div>
            {errors?.sph?.msg && <div className="invalid-sph">{errors?.sph?.msg}</div>}
          </div>
        </div>
      </div>
    </OpticalVariantStyled>
  );
};

OpticalVariant.propTypes = {
  sphOptions: PropTypes.array.isRequired,
  selectedSPHSkus: PropTypes.object.isRequired,
  setSelectedSPHSkus: PropTypes.func.isRequired,
  variants: PropTypes.array.isRequired,
  errors: PropTypes.object,
  productDetails: PropTypes.object.isRequired,
};

OpticalVariant.defaultProps = {
  errors: {},
};

export default OpticalVariant;
